import React, { useEffect } from "react";
import { Users, Plus, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { checkUserPermissions } from "../../../../utils/checkUserPermissions";
import { accessTitanversePatientUpsert } from "../../../../data/constantStrings";
import {
  setShowPermissionMessage,
  setShowPermissionModal,
} from "@/dispatchers/appDispatchers";
interface PatientsListEmptyStateProps {
  isDarkMode: boolean;
  onAddPatient: () => void;
  searchValue?: string;
  includeTitanPatients?: boolean;
  onIncludeTitanPatientsChange?: (value: boolean) => void;
  searchTitanEnabled?: boolean;
  setTriggerTitanSearch: (value: boolean) => void;
}

export default function PatientsListEmptyState({
  isDarkMode,
  onAddPatient,
  searchValue,
  includeTitanPatients,
  onIncludeTitanPatientsChange,
  searchTitanEnabled,
  setTriggerTitanSearch,
}: PatientsListEmptyStateProps) {
  useEffect(() => {
    if (
      searchValue.trim() === "" &&
      includeTitanPatients &&
      onIncludeTitanPatientsChange
    ) {
      onIncludeTitanPatientsChange(false);
    }
  }, [searchValue, includeTitanPatients, onIncludeTitanPatientsChange]);
  const canAddPatient = checkUserPermissions(accessTitanversePatientUpsert);
  // Hide Search Titan button if searchTitanEnabled is on (it's automatic)
  const showSearchTitanButton =
    searchValue.trim() !== "" && !includeTitanPatients && !searchTitanEnabled;

  return (
    <div className="flex items-center justify-center h-32 mt-6">
      <div
        className={`text-center transition-colors duration-300 ${
          isDarkMode ? "text-gray-400" : "text-gray-500"
        }`}
      >
        <Users className="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p>No patients found</p>
        <p className="mb-4 text-sm">
          Try adjusting your filters or search terms
        </p>
        <div className="flex items-center justify-center gap-2">
          <Button
            onClick={() => {
              if (!canAddPatient) {
                setShowPermissionMessage("Create or update patient");
                setShowPermissionModal(true);
                return;
              }
              onAddPatient();
            }}
            size="sm"
            className={`bg-blue-600 hover:bg-blue-700 ${
              !canAddPatient ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Patient
          </Button>

          {/* Search Titan Button */}
          {showSearchTitanButton && onIncludeTitanPatientsChange && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!canAddPatient) {
                  setShowPermissionMessage("Create or update patient");
                  setShowPermissionModal(true);
                  return;
                }

                onIncludeTitanPatientsChange(true);
                setTriggerTitanSearch(true);
              }}
              className={`${
                isDarkMode
                  ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                  : "bg-white border-gray-300 hover:bg-gray-50"
              } ${!canAddPatient ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Database className="w-4 h-4 mr-2" />
              Search Titan
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
